import React from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/arvix/arvix_logo.png";
import { FaFacebook, FaInstagram, FaPhone, FaMapMarkerAlt, FaEnvelope, FaCode, FaArrowRight } from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import { services, contactInfo, navLinks } from "../../config/constants";

const Footer = () => {
    return (
        <footer className="relative overflow-hidden bg-designer-dark text-white">
            <div className="absolute inset-0 texture-grid-dark opacity-60" aria-hidden="true" />
            <div
                className="absolute -top-32 right-[-10%] w-[34rem] h-[34rem] rounded-full bg-designer-primary/20 blur-[120px] pointer-events-none"
                aria-hidden="true"
            />

            <div className="relative container-responsive">
                {/* CTA band */}
                <div className="pt-14 md:pt-20 pb-10 md:pb-12 border-b border-white/10">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                        <div className="max-w-2xl">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="tick" aria-hidden="true" />
                                <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/60">
                                    From idea to impact
                                </p>
                            </div>
                            <p className="text-balance font-display font-extrabold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
                                Have a brand to build?{" "}
                                <span className="text-designer-primary">Let's talk.</span>
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                            <Link
                                to="/contact"
                                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-designer-primary text-white font-semibold text-sm rounded-full hover:bg-red-600 transition-colors"
                            >
                                Start a Project
                                <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a
                                href="https://wa.me/8801645076455"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/25 text-white font-semibold text-sm rounded-full hover:border-white/60 hover:bg-white/5 transition-all"
                            >
                                WhatsApp Us
                            </a>
                        </div>
                    </div>
                </div>

                {/* Link columns */}
                <div className="py-10 md:py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr] gap-10 lg:gap-8">
                    {/* Brand */}
                    <div>
                        <img
                            src={logo}
                            alt="Arvix Creative - Art Vision Experience - Creative Studio"
                            className="h-10 md:h-11 w-auto brightness-0 invert"
                            loading="lazy"
                            decoding="async"
                        />
                        <p className="mt-2 text-xs text-white/40 italic">
                            <span className="font-semibold text-designer-accent">ArViX</span> = Art Vision Experience
                        </p>
                        <p className="mt-4 text-sm text-white/60 leading-relaxed max-w-xs">
                            Creating stunning visuals that communicate value and connect with audiences.
                            Your all-in-one creative partner.
                        </p>
                        <div className="mt-6 flex gap-2.5">
                            <a
                                href={contactInfo.socialMedia.facebook}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Arvix Creative on Facebook"
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-designer-primary hover:bg-designer-primary transition-all duration-300"
                            >
                                <FaFacebook size={17} />
                            </a>
                            <a
                                href={contactInfo.socialMedia.instagram}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Arvix Creative on Instagram"
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-designer-primary hover:bg-designer-primary transition-all duration-300"
                            >
                                <FaInstagram size={17} />
                            </a>
                        </div>
                    </div>

                    {/* Explore */}
                    <nav aria-label="Footer">
                        <h3 className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/40 mb-5">
                            Explore
                        </h3>
                        <ul className="space-y-2.5">
                            {navLinks.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.path}
                                        className="text-sm text-white/70 hover:text-white transition-colors"
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Services */}
                    <div>
                        <h3 className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/40 mb-5">
                            Services
                        </h3>
                        <ul className="space-y-2.5">
                            {services.slice(0, 6).map((service, index) => (
                                <li key={index} className="text-sm text-white/70">
                                    {service.title}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/40 mb-5">
                            Get in touch
                        </h3>
                        <ul className="space-y-3.5 text-sm">
                            <li>
                                <a
                                    href="https://wa.me/8801645076455"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-3 text-white/70 hover:text-white transition-colors"
                                    title="Chat with us on WhatsApp"
                                >
                                    <FaPhone className="mt-0.5 text-designer-primary text-xs shrink-0" />
                                    {contactInfo.phone} (Call or WhatsApp)
                                </a>
                            </li>
                            <li>
                                <a
                                    href="https://maps.app.goo.gl/5jRZ3P2BadXRheqV9"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-3 text-white/70 hover:text-white transition-colors"
                                    title="View location on Google Maps"
                                >
                                    <FaMapMarkerAlt className="mt-0.5 text-designer-primary text-xs shrink-0" />
                                    {contactInfo.location}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${contactInfo.email}`}
                                    className="flex items-start gap-3 text-white/70 hover:text-white transition-colors break-all"
                                >
                                    <FaEnvelope className="mt-0.5 text-designer-primary text-xs shrink-0" />
                                    {contactInfo.email}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="py-6 border-t border-white/10">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:gap-5">
                            <p className="text-white/50 text-[13px]">
                                © {new Date().getFullYear()} Arvix Creative. All rights reserved.
                            </p>
                            <div className="flex gap-5 text-[13px]">
                                <Link to="/privacy" className="text-white/50 hover:text-white transition-colors">
                                    Privacy Policy
                                </Link>
                                <Link to="/terms" className="text-white/50 hover:text-white transition-colors">
                                    Terms of Service
                                </Link>
                            </div>
                        </div>
                        <a
                            href="https://junaidul.pro.bd/codejborg"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Developed by CodeJBorg: visit developer website"
                            className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 py-1.5 pr-3 pl-1.5 font-mono text-[11px] tracking-wide text-white/60 transition-colors duration-300 hover:border-designer-primary/60 hover:text-white"
                        >
                            <span className="flex size-6 items-center justify-center rounded-full bg-designer-primary text-white">
                                <FaCode className="size-3.5" aria-hidden="true" />
                            </span>
                            <span>Developed by</span>
                            <span className="font-semibold text-white">
                                <span className="text-designer-primary">&lt;</span>CodeJBorg<span className="text-designer-primary"> /&gt;</span>
                            </span>
                            <FiArrowUpRight className="size-3.5 opacity-40 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100" aria-hidden="true" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
